import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tag, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { staffApi } from "api/staff.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QueryParam } from "types/query";
import { Staff } from "types/staff";
import { getTitle } from "utils";
import { $url } from "utils/url";
import { StaffModal } from "./components/StaffModal";
import { ResetPasswordModal } from "views/Staff/components/ResetPasswordModal";
import { userStore } from "store/userStore";
import { can } from "utils/auth";

const staffPath = "/staff";

export const StaffPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Staff[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [selectedStaffs, setSelectedStaffs] = useState<Staff[]>([]);

  const resetPasswordModalRef = useRef<ResetPasswordModal>(null);
  const staffModalRef = useRef<StaffModal>(null);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: any[], records: Staff[]) => {
      setSelectedRowKeys(keys);
      setSelectedStaffs(records);
    },
  };
  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await staffApi.findAll(queryParams);
      setData(res.data.staffs);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(
    async (row: Staff) => {
      setLoading(true);
      try {
        await staffApi.delete(row.id);
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const handleBlock = useCallback(
    async (staffs: Staff[], block: boolean) => {
      try {
        for (let i = 0; i < staffs.length; i++) {
          const staff = staffs[i];
          await staffApi.block(staff.id, {
            staff: {
              name: staff.name,
              avatar: staff.avatar,
              phone: staff.phone,
              email: staff.email,
              isBlocked: block,
            },
            roleId: staff.role.id,
          });
        }
        if (block) {
          message.success("Khóa người dùng thành công");
        } else if (block === false) {
          message.success("Mở khóa người dùng thành công");
        }
      } finally {
        setLoading(false);
        await fetchData(query);
        setSelectedStaffs([]);
        setSelectedRowKeys([]);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useCallback(
    debounce((keyword: string) => setQuery({ ...query, search: keyword, page: 1, limit: 20 }), 300),
    [setQuery, query],
  );

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const canCreateStaff = can(staffPath, "CREATE");
  const canEditStaff = can(staffPath, "UPDATE");
  const canDeleteStaffPath = can(staffPath, "DELETE");
  const canBlockStaffPath = can(staffPath, "BLOCK");
  const canResetPasswordStaffPath = can(staffPath, "RESET_PASSWORD");

  const columns = useMemo<TableColumnsType<Staff>>(
    () => [
      {
        title: "Username",
        dataIndex: "username",
        key: "username",
        width: 250,
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <INA image={record.avatar ? $url(record.avatar) : undefined} name={record.username || "--"} />
            {record.isBlocked && <Tag color="red">Bị khóa</Tag>}
          </div>
        ),
      },
      {
        title: "Họ tên",
        dataIndex: "name",
        key: "name",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Quyền",
        key: "role",
        width: 180,
        render: (_, record) => <TableContent>{record.role?.name || "--"}</TableContent>,
      },
      ...(canDeleteStaffPath || canEditStaff || canBlockStaffPath || canResetPasswordStaffPath
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 160,
            align: "center" as const,
            render: (_: any, record: Staff) => (
              <div className="flex gap-2 justify-center items-center">
                {canEditStaff && (
                  <Tooltip title="Cập nhật">
                    <ActionButton
                      aria-label="Cập nhật"
                      onClick={() => {
                        staffModalRef.current?.handleUpdate(record);
                      }}
                    >
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDeleteStaffPath && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa?"
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
                {canBlockStaffPath && (
                  <Tooltip title={record.isBlocked ? "Mở khóa" : "Khóa"}>
                    <Popconfirm
                      cancelText="Không"
                      okText={record.isBlocked ? "Bỏ" : "Khóa"}
                      onConfirm={() => {
                        handleBlock([record], !record.isBlocked);
                      }}
                      title={`Xác nhận ${record.isBlocked ? "bỏ" : ""} khóa?`}
                    >
                      <ActionButton aria-label={record.isBlocked ? "Mở khóa" : "Khóa"}>
                        {record.isBlocked ? (
                          <UnlockOutlined style={{ color: "#22c55e" }} />
                        ) : (
                          <LockOutlined style={{ color: "#f59e0b" }} />
                        )}
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
                {canResetPasswordStaffPath && (
                  <Tooltip title="Reset mật khẩu">
                    <ActionButton
                      aria-label="Reset mật khẩu"
                      onClick={() => {
                        resetPasswordModalRef.current?.handleCreate(record);
                      }}
                    >
                      <KeyOutlined style={{ color: "#2563eb" }} />
                    </ActionButton>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [
      handleBlock,
      handleDelete,
      canDeleteStaffPath,
      canEditStaff,
      canBlockStaffPath,
      canResetPasswordStaffPath,
    ],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm theo họ tên",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              canCreateStaff && (
                <AppButton
                  tone="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    staffModalRef.current?.handleCreate();
                  }}
                >
                  Tạo mới
                </AppButton>
              )
            }
            actions={
              canBlockStaffPath && (
                <Popconfirm
                  cancelText="Không"
                  okText="Khóa"
                  onConfirm={() => handleBlock(selectedStaffs, true)}
                  title="Xác nhận khóa?"
                >
                  <AppButton
                    tone="light"
                    icon={<LockOutlined />}
                    disabled={!selectedRowKeys.length}
                    loading={loading}
                  >
                    Khóa
                  </AppButton>
                </Popconfirm>
              )
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Staff>
            rowSelection={rowSelection}
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              setQuery((prev) => ({ ...prev, page, limit }));
            }}
          />
        }
      />

      <ResetPasswordModal
        ref={resetPasswordModalRef}
        onClose={() => { }}
        onSubmitOk={() => fetchData(query)}
      />

      <StaffModal ref={staffModalRef} onSubmitOk={() => fetchData(query)} onClose={() => { }} />
    </div>
  );
});
